import React, { useRef } from 'react'

import { FiDownload } from 'react-icons/fi'
import { HiOutlineArrowsExpand, HiOutlineCog, HiOutlineRefresh } from 'react-icons/hi'
import { ImSpinner2 } from 'react-icons/im'
import { RiDraggable } from 'react-icons/ri'

import { Skeleton, TruncateText, Tooltip } from '@/components/ui'
import ErrorBoundary from '@/insights/common/ErrorBoundary'
import type { CardSize } from '@/types/dashboard-types'

//TODO jellyfish-ui: add positioning element to anu's tooltip so we can universally use that.

export interface ConfigurableCardType {
  width: CardSize
  aspectRatio: number
  position?: {
    lg?: { x: number; y: number; w: number; h: number }
    md?: { x: number; y: number; w: number; h: number }
    sm?: { x: number; y: number; w: number; h: number }
  }
}

export interface CardWithId {
  id: string
  element: React.ReactElement
  config: ConfigurableCardType
}

export interface ConfigurableCardProps {
  id?: string
  error: string
  title: string // the user title for this instance of the insight
  loading: boolean
  caption?: React.ReactNode
  content: React.ReactNode
  footer?: React.ReactNode
  onConfigure: () => void
  onRefresh: () => void
  originalTitle?: string
  onExportCsv?: () => void
  csvExportLoading?: boolean
  inEditMode?: boolean
  isRefreshing?: boolean
  onFullscreen?: () => void
}

// Drag Handle Component with proper typing
export const DragHandle = () => {
  return (
    <>
      <Tooltip
        children={
          <button
            className="cursor-grab active:cursor-grabbing  rounded   transition-all mr-2 btn btn-ghost p-2 h-fit drag-handle"
            style={{ touchAction: 'none' }}
          >
            <RiDraggable size={20} />
          </button>
        }
        tip={'Drag to reorder'}
      />
    </>
  )
}

// TODO: This doesn't work with the div wrapper?
export const ConfigurableCardFallback = () => (
  <div aria-hidden className="" data-testid="fallback-component">
    <Skeleton shouldRemoveCurvedBorder />
  </div>
)

export default function ConfigurableCard({
  id,
  title,
  content,
  error,
  caption,
  loading,

  footer,

  onConfigure,
  onRefresh,
  onExportCsv,
  csvExportLoading = false,

  inEditMode,
  isRefreshing,
  onFullscreen,
}: ConfigurableCardProps) {
  const cardRef = useRef<HTMLElement>(null)

  if (loading) {
    return <ConfigurableCardFallback />
  }

  const footerIconSize = 17

  return (
    <section
      id={id || undefined}
      ref={cardRef}
      className={` w-full h-full flex flex-col  bg-base-100 rounded-lg overflow-visible`}
      data-testid="configurable-card-section"
    >
      {/* CARD TITLE */}
      <div className={'flex-col items-start w-full '}>
        <div className={`w-full flex  items-center justify-between  px-4 py-2 `}>
          <div className="flex items-center flex-1 min-w-0 w-full ">
            {inEditMode && <DragHandle />}

            <div className={`flex flex-col flex-1 min-w-0  relative `}>
              <div className={`flex flex-row  max-w-fit justify-start items-center `}>
                <TruncateText
                  // parentRef={cardRef}
                  tooltipPosition="bottom"
                  wrapperType="truncate"
                  tooltipText={title}
                  renderItem={(isTruncated) => (
                    <h4 className={`${isTruncated ? 'truncate' : ''} text-primary font-bold text-md`}>
                      {title.toUpperCase()}
                    </h4>
                  )}
                />
              </div>

              <TruncateText
                tooltipPosition="bottom"
                tooltipText={typeof caption === 'string' ? caption : undefined}
                wrapperType="truncate"
                renderItem={(isTruncated) => {
                  return caption ? (
                    <div className={`${isTruncated ? 'truncate' : ''} text-sm text-base-content font-light `}>
                      {caption}
                    </div>
                  ) : null
                }}
              />
            </div>
          </div>

          <div className="tooltip tooltip-bottom z-20 w-fit flex" data-tip="Settings">
            <button onClick={onConfigure} className="btn btn-ghost p-2 h-fit">
              <HiOutlineCog size={20} />
            </button>
          </div>
        </div>
      </div>
      {/* END CARD TITLE */}

      <div className={`flex flex-col flex-1 overflow-hidden `}>
        <div className="flex-1 overflow-y-auto min-h-0">
          {error && (
            <div className="w-full items-center">
              <div className="alert alert-error text-white mr-6 ml-6 mt-2">
                <strong>Error - </strong> {error}
              </div>
            </div>
          )}
          <ErrorBoundary>{!error && <div className="flex w-full h-full flex-col">{content}</div>}</ErrorBoundary>
        </div>

        {/* CARD FOOTER */}
        <footer className={`flex px-4 py-1 justify-between w-full items-center shrink-0 border-t border-base-200 `}>
          <div className="w-full text-sm text-base-content/70 font-light">{footer || ''}</div>

          <div className="flex  gap-2 overflow-visible">
            {onExportCsv && (
              <div className="tooltip w-fit" data-tip={csvExportLoading ? 'Exporting CSV...' : 'Export CSV'}>
                <button
                  className={`btn btn-ghost p-1 h-fit ${csvExportLoading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                  onClick={csvExportLoading ? undefined : onExportCsv}
                  disabled={csvExportLoading}
                >
                  {csvExportLoading ? (
                    <ImSpinner2 size={footerIconSize} className="animate-spin" />
                  ) : (
                    <FiDownload size={footerIconSize} />
                  )}
                </button>
              </div>
            )}

            <div className="tooltip w-fit tooltip-left" data-tip="Refresh Data">
              <button
                className="btn btn-ghost cursor-pointer p-1 h-fit"
                onClick={() => onRefresh()}
                disabled={isRefreshing}
              >
                <HiOutlineRefresh
                  size={footerIconSize}
                  className={`${isRefreshing ? 'animate-spin transition-all duration-200' : ''}`}
                />
              </button>
            </div>

            {onFullscreen && (
              <div className="tooltip w-fit tooltip-left" data-tip="Fullscreen">
                <button className="btn btn-ghost cursor-pointer p-1 h-fit" onClick={onFullscreen}>
                  <HiOutlineArrowsExpand size={footerIconSize} />
                </button>
              </div>
            )}
          </div>
        </footer>
      </div>
    </section>
  )
}
